// AUTO-GENERATED from docs/TECHNICAL_PRD.md — CID Agent PWA API layer
// Module: Booking Forms (OTP/OTR/ALS/ALR) + e-signing
// Every list (collection GET) takes params { page, limit, ...filters }.
import axios from 'axios';
import client from './client';

/**
 * GET /api/agent/booking-forms [LIST · paginated]
 * List the caller agent's booking forms (OTP/OTR/ALS/ALR) with pagination, search, and status/type filters for the agent's forms dashboard.
 * @disposition ADAPT_SCOPE · maps to GET /api/booking-forms (controller.listForms / service.listForms)
 */
export const listBookingForms = (params = {}) => client.get(`/api/agent/booking-forms`, { params });

/**
 * GET /api/agent/booking-forms/:id
 * Full form detail incl. derived signatories, per-signatory signing status, and signing tokens, for the fill/sign/track screen.
 * @disposition ADAPT_SCOPE · maps to GET /api/booking-forms/:id (controller.getFormById / service.getFormById)
 */
export const getBookingFormsById = (id, params = {}) => client.get(`/api/agent/booking-forms/${id}`, { params });

/**
 * GET /api/agent/booking-forms/:id/preview [LIST · paginated]
 * Server-rendered HTML of the saved form for on-screen preview and PDF download.
 * @disposition ADAPT_SCOPE · maps to GET /api/booking-forms/:id/preview (controller.getFormPreview / service.getFormPreviewHtml)
 */
export const listBookingFormsPreview = (id, params = {}) => client.get(`/api/agent/booking-forms/${id}/preview`, { params });

/**
 * POST /api/agent/booking-forms/:id/number-lou
 * Print / PDF on an agreement: give its attached Letter of Undertaking a number before the
 * paper copy is printed. Returns { lou_ref, numbered, missing? } — not numbered while the
 * agreement is incomplete (`missing`) or already numbered.
 */
export const createBookingFormsNumberLou = (id) => client.post(`/api/agent/booking-forms/${id}/number-lou`);

/**
 * POST /api/agent/booking-forms/preview
 * Live HTML preview from unsaved form_data while the agent is still filling terms.
 * @disposition ADAPT_SCOPE · maps to POST /api/booking-forms/preview (controller.renderPreview / service.renderPreviewHtml)
 */
export const createBookingFormsPreview = (data = {}) => client.post(`/api/agent/booking-forms/preview`, data);

/**
 * POST /api/agent/booking-forms
 * Create a DRAFT booking form (OTP/OTR/ALS/ALR), optionally from a listing, stamped as agent-originated so it becomes visible/editable only to the caller.
 * @disposition BUILD_NEW · maps to POST /api/booking-forms (controller.createForm / service.createForm)
 */
export const createBookingForms = (data = {}) => client.post(`/api/agent/booking-forms`, data);

/**
 * PUT /api/agent/booking-forms/:id
 * Edit terms of a DRAFT form (fill/adjust form_data) before signing.
 * @disposition ADAPT_SCOPE · maps to PUT /api/booking-forms/:id (controller.updateForm / service.updateForm)
 */
export const updateBookingFormsById = (id, data = {}) => client.put(`/api/agent/booking-forms/${id}`, data);

/**
 * POST /api/agent/booking-forms/:id/send-signing-link
 * Email a remote OTP-gated signing link to a signatory (purchaser/owner/tenant/landlord/witness).
 * @disposition ADAPT_SCOPE · maps to POST /api/booking-forms/:id/send-signing-link (service.sendSigningLink)
 */
export const createBookingFormsSendSigningLinkById = (id, data = {}) => client.post(`/api/agent/booking-forms/${id}/send-signing-link`, data);

/**
 * POST /api/agent/booking-forms/:id/send-fill-sign-link
 * Invite one SIDE of an agreement (purchaser/owner on a sale, tenant/landlord on a rent) to
 * fill in its OWN party details and sign. `data` is { side, email, hide_counterparty?, forward_email? }:
 * `hide_counterparty` (default true) redacts the other side out of the copy they read, and
 * `forward_email` sends the other side's link automatically once this one signs.
 * Replies with the same full detail payload GET /:id returns, as `form`.
 * @disposition BUILD_NEW · maps to POST /api/booking-forms/:id/send-fill-sign-link (service.sendFillSignLink)
 */
export const createBookingFormsSendFillSignLinkById = (id, data = {}) => client.post(`/api/agent/booking-forms/${id}/send-fill-sign-link`, data);

/**
 * POST /api/agent/booking-forms/:id/revoke-signing-link
 * Revoke a still-pending signing link for a signatory.
 * @disposition ADAPT_SCOPE · maps to POST /api/booking-forms/:id/revoke-signing-link (service.revokeSigningLink)
 */
export const createBookingFormsRevokeSigningLinkById = (id, data = {}) => client.post(`/api/agent/booking-forms/${id}/revoke-signing-link`, data);

/**
 * POST /api/agent/booking-forms/:id/revoke-signature
 * Revoke an already-captured signature, reverting the signatory to unsigned and the form SIGNED/PENDING_APPROVAL -> PENDING_SIGNING.
 * @disposition ADAPT_SCOPE · maps to POST /api/booking-forms/:id/revoke-signature (service.revokeSignature)
 */
export const createBookingFormsRevokeSignatureById = (id, data = {}) => client.post(`/api/agent/booking-forms/${id}/revoke-signature`, data);

/**
 * POST /api/agent/booking-forms/:id/on-device-sign
 * Capture an in-person signature drawn on the agent's device (pass-the-phone), incl. witness self-entry of name/NRIC; auto-advances form to SIGNED when all required signatories are done.
 * @disposition ADAPT_SCOPE · maps to POST /api/booking-forms/:id/on-device-sign (service.recordOnDeviceSignature)
 */
export const createBookingFormsOnDeviceSignById = (id, data = {}) => client.post(`/api/agent/booking-forms/${id}/on-device-sign`, data);

/**
 * PATCH /api/agent/booking-forms/:id/witness
 * Fill in / correct a witness's name + NRIC into form_data (DRAFT/PENDING_SIGNING/SIGNED, witness not yet signed).
 * @disposition ADAPT_SCOPE · maps to PATCH /api/booking-forms/:id/witness (service.updateWitnessInfo)
 */
export const patchBookingFormsWitnessById = (id, data = {}) => client.patch(`/api/agent/booking-forms/${id}/witness`, data);

/**
 * POST /api/agent/booking-forms/:id/issue
 * Put the agent's own form into force (DRAFT / PENDING_SIGNING / SIGNED -> issued).
 * Nothing is submitted to anyone — there is no approver. Locks the linked listing
 * UNDER_OFFER if the form names one.
 * @disposition ADAPT_SCOPE · maps to POST /api/booking-forms/:id/issue (service.issueForm)
 */
export const createBookingFormsIssueById = (id, data = {}) => client.post(`/api/agent/booking-forms/${id}/issue`, data);

/**
 * POST /api/agent/booking-forms/:id/cancel
 * Cancel a dead-deal form the agent owns (deal fell through) without needing HQ.
 * @disposition ADAPT_SCOPE · maps to POST /api/booking-forms/:id/cancel (service.cancelForm)
 */
export const createBookingFormsCancelById = (id, data = {}) => client.post(`/api/agent/booking-forms/${id}/cancel`, data);

/**
 * POST /api/agent/booking-forms/:id/reopen
 * A form out for signature but not issued goes back to an editable DRAFT (same ref).
 * Every signature captured and every link sent is cancelled — everyone signs again.
 */
export const createBookingFormsReopenById = (id) => client.post(`/api/agent/booking-forms/${id}/reopen`);

/**
 * POST /api/agent/booking-forms/:id/revise
 * An issued form: start its revision — a new DRAFT copy with its own ref — or get the one
 * already open. The issued form stays in force until the revision is issued.
 */
export const createBookingFormsReviseById = (id) => client.post(`/api/agent/booking-forms/${id}/revise`);

/**
 * POST /api/agent/booking-forms/:id/convert-to-sale
 * Agent self-converts their issued OTP/OTR into a sale (mirrors the admin flow; HQ still
 * reviews and converts the sale itself). `data` is the sale the agent completed on the
 * create screen, prefilled from the form — deal type, property, price, parties, agents.
 * Sending nothing still works and derives a minimal sale from the form alone.
 * @disposition BUILD_NEW · maps to POST /:id/convert-to-sale (service.convertToSale)
 */
export const createBookingFormsConvertToSaleById = (id, data = {}) => client.post(`/api/agent/booking-forms/${id}/convert-to-sale`, data);

// ── Public (signatory-facing) OTP signing flow — tokenized, UNAUTHENTICATED. Served by the
// agent server's /api/public/sign/* and consumed by the /sign/:token page on the customer's
// own device. Uses a BARE axios client: no agent bearer token, no 401→login redirect.
const pub = axios.create({ baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001', timeout: 20000 });
pub.interceptors.response.use(
  (res) => res.data, // unwrap the { success, message, data } envelope like the main client
  (error) => {
    const env = error.response?.data;
    const e = new Error(env?.error?.message || env?.message || error.message || 'Request failed');
    e.status = error.response?.status;
    return Promise.reject(e);
  }
);

/** GET /api/public/sign/:token — validate the link; masked email until OTP verified. */
export const getPublicSign = (token, params = {}) => pub.get(`/api/public/sign/${token}`, { params });
/** POST /api/public/sign/:token/verify-otp — verify the 6-digit code → full info + document. */
export const createPublicSignVerifyOtp = (token, data = {}) => pub.post(`/api/public/sign/${token}/verify-otp`, data);
/** POST /api/public/sign/:token/resend-otp — (re)send the OTP to the signatory's email. */
export const createPublicSignResendOtp = (token, data = {}) => pub.post(`/api/public/sign/${token}/resend-otp`, data);
/** POST /api/public/sign/:token/submit — store the signature; the server stamps signed_at. */
export const createPublicSignSubmit = (token, data = {}) => pub.post(`/api/public/sign/${token}/submit`, data);
